/**
 * Phase MGR1 — Manager landing page (mobile-first).
 *
 * Big unit-number search at the top, customer-name fallback toggle, and
 * a "Recent / ending soon" list below. Tap any row to drill into the
 * rental detail page where inspection + record-return happens.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Search, LogOut, Truck, ArrowRight, CheckCircle2 } from "lucide-react";
import OfflineBanner from "@/pwa/OfflineBanner";
import PwaInstallPrompt from "@/pwa/PwaInstallPrompt";
import InspectionQueueBadge from "@/pwa/InspectionQueueBadge";

function formatDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

function StatusPill({ rental }) {
    if (rental.derived_status === "awaiting_closure") {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 text-[10px] font-semibold tracking-wide uppercase">
                <CheckCircle2 size={10} />
                Awaiting closure
            </span>
        );
    }
    if (rental.inspection_submitted) {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-900 text-[10px] font-semibold tracking-wide uppercase">
                Inspection done
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-900 text-[10px] font-semibold tracking-wide uppercase">
            Active
        </span>
    );
}

function RentalCard({ rental, onSelect }) {
    return (
        <button
            type="button"
            onClick={() => onSelect(rental.rental_id)}
            data-testid={`manager-rental-row-${rental.rental_id}`}
            className="w-full text-left p-4 rounded-xl border border-slate-200 bg-white hover:border-blue-300 active:scale-[0.99] transition-all flex items-start gap-3"
        >
            <div className="shrink-0 h-10 w-10 rounded-lg bg-blue-100 text-blue-900 flex items-center justify-center">
                <Truck size={18} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-slate-900 truncate">
                        {rental.customer?.display_name || "—"}
                    </div>
                    <StatusPill rental={rental} />
                </div>
                <div className="text-sm text-slate-600 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">
                        {rental.equipment?.unit_number || "no unit#"}
                    </span>
                    <span className="text-slate-400">·</span>
                    <span>End {formatDate(rental.end_date)}</span>
                </div>
            </div>
            <ArrowRight size={18} className="text-slate-400 self-center shrink-0" />
        </button>
    );
}

export default function ManagerLandingPage() {
    const { user, logout } = useAuth();
    const { tenant } = useTenant();
    const navigate = useNavigate();

    const [searchMode, setSearchMode] = useState("unit"); // "unit" | "customer"
    const [query, setQuery] = useState("");
    const [searching, setSearching] = useState(false);
    const [results, setResults] = useState(null);
    const [recent, setRecent] = useState([]);
    const [loadingRecent, setLoadingRecent] = useState(true);

    const loadRecent = useCallback(async () => {
        setLoadingRecent(true);
        try {
            const { data } = await api.get("/manager/recent");
            setRecent(data || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoadingRecent(false);
        }
    }, []);

    useEffect(() => { loadRecent(); }, [loadRecent]);

    const onSubmit = async (e) => {
        e?.preventDefault?.();
        const q = query.trim();
        if (!q) return;
        setSearching(true);
        try {
            const path = searchMode === "unit"
                ? `/manager/rentals/lookup?unit_number=${encodeURIComponent(q)}`
                : `/manager/rentals/search?q=${encodeURIComponent(q)}`;
            const { data } = await api.get(path);
            setResults(data || []);
            if ((data || []).length === 1) {
                navigate(`/manager/rentals/${data[0].rental_id}`);
            } else if ((data || []).length === 0) {
                // MGR1 follow-up — softer empty-state. The lookup logic is
                // sound (case-insensitive + dash/space normalization), so a
                // miss usually means the unit_number was never recorded on
                // the rental. Point the user at the customer-name fallback.
                toast.message(searchMode === "unit"
                    ? `No active rentals match unit number "${q}". Try searching by customer name instead.`
                    : `No customer matches "${q}". Try a different spelling or partial name.`);
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSearching(false);
        }
    };

    const onLogout = () => { logout(); navigate("/login", { replace: true }); };

    const displayName = tenant?.display_name || "RentFlow";
    const logoUrl = tenant?.logo_url || "/favicon.png";

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col">
            {/* Phase PWA — offline banner sits above sticky header. */}
            <OfflineBanner />
            {/* Sticky header — small + dense for mobile */}
            <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
                <div className="max-w-xl mx-auto px-4 h-14 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                        <img
                            src={logoUrl}
                            alt={displayName}
                            data-testid="manager-tenant-logo"
                            className="h-7 w-auto max-w-[100px] object-contain rounded"
                        />
                        <div className="leading-tight">
                            <div data-testid="manager-tenant-name" className="font-display font-bold text-sm tracking-tight text-slate-900 truncate">
                                {displayName}
                            </div>
                            <div className="text-[9px] uppercase tracking-[0.18em] text-blue-900">
                                Yard
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <InspectionQueueBadge />
                        <button
                            type="button"
                            onClick={onLogout}
                            data-testid="manager-logout"
                            className="text-slate-500 hover:text-slate-900 active:text-blue-900 p-2 -mr-2"
                            aria-label="Sign out"
                        >
                            <LogOut size={18} />
                        </button>
                    </div>
                </div>
            </header>

            <main className="flex-1 max-w-xl w-full mx-auto px-4 py-5 space-y-6">
                {/* Phase PWA — install CTA only on supported browsers. */}
                <PwaInstallPrompt />
                <section data-testid="manager-search-section">
                    <h1 className="font-display text-2xl font-bold text-slate-900 mb-1">
                        Find a rental
                    </h1>
                    <p className="text-sm text-slate-500 mb-3">
                        {searchMode === "unit"
                            ? "Type the unit number on the trailer."
                            : "Type any part of the customer's name."}
                    </p>
                    <form onSubmit={onSubmit} className="space-y-3">
                        <Input
                            data-testid={searchMode === "unit" ? "manager-unit-search" : "manager-customer-search"}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            autoCapitalize={searchMode === "unit" ? "characters" : "words"}
                            autoComplete="off"
                            spellCheck={false}
                            inputMode="text"
                            placeholder={searchMode === "unit" ? "Enter unit number" : "Enter customer name"}
                            className="h-14 text-lg font-mono"
                        />
                        <Button
                            type="submit"
                            data-testid="manager-search-submit"
                            disabled={searching || !query.trim()}
                            className="w-full h-14 text-base font-semibold bg-blue-900 hover:bg-blue-800 gap-2"
                        >
                            <Search size={18} />
                            {searching ? "Searching…" : "Search"}
                        </Button>
                    </form>
                    <button
                        type="button"
                        data-testid="manager-search-by-customer"
                        onClick={() => {
                            setSearchMode((m) => (m === "unit" ? "customer" : "unit"));
                            setQuery("");
                            setResults(null);
                        }}
                        className="mt-3 text-sm text-blue-900 underline-offset-2 hover:underline"
                    >
                        {searchMode === "unit"
                            ? "Can't find it? Search by customer name"
                            : "Search by unit number instead"}
                    </button>
                </section>

                {results !== null && (
                    <section data-testid="manager-results-section">
                        <h2 className="font-display text-base font-semibold text-slate-700 mb-2 uppercase tracking-wider">
                            Results
                        </h2>
                        {results.length === 0 ? (
                            <div
                                data-testid="manager-results-empty"
                                className="rounded-md border border-slate-200 bg-white px-4 py-4 text-sm text-slate-700 space-y-2"
                            >
                                <p className="font-semibold text-slate-900">
                                    No active rentals match {searchMode === "unit" ? "unit" : "customer"} "{query.trim()}".
                                </p>
                                {searchMode === "unit" ? (
                                    <p className="text-slate-600">
                                        Tip: try{" "}
                                        <button
                                            type="button"
                                            data-testid="manager-empty-switch-to-customer"
                                            onClick={() => {
                                                setSearchMode("customer");
                                                setQuery("");
                                                setResults(null);
                                            }}
                                            className="text-blue-900 font-semibold underline-offset-2 hover:underline"
                                        >
                                            searching by customer name
                                        </button>
                                        {" "}instead. If you expected a match, the equipment's unit number may not be on file — ask an admin to add it on the rental's detail page.
                                    </p>
                                ) : (
                                    <p className="text-slate-600">
                                        Tip: try a partial name or a different spelling.
                                    </p>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {results.map((r) => (
                                    <RentalCard
                                        key={r.rental_id}
                                        rental={r}
                                        onSelect={(id) => navigate(`/manager/rentals/${id}`)}
                                    />
                                ))}
                            </div>
                        )}
                    </section>
                )}

                <section data-testid="manager-recent-section">
                    <h2 className="font-display text-base font-semibold text-slate-700 mb-2 uppercase tracking-wider">
                        Recent / ending soon
                    </h2>
                    {loadingRecent ? (
                        <div className="text-sm text-slate-500 py-4">Loading…</div>
                    ) : recent.length === 0 ? (
                        <div className="text-sm text-slate-500 py-4" data-testid="manager-recent-empty">
                            Nothing recent. Try the search above.
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {recent.map((r) => (
                                <RentalCard
                                    key={r.rental_id}
                                    rental={r}
                                    onSelect={(id) => navigate(`/manager/rentals/${id}`)}
                                />
                            ))}
                        </div>
                    )}
                </section>

                <div className="text-center text-[11px] text-slate-400 pt-4">
                    Signed in as {user?.full_name} · {user?.email}
                </div>
            </main>
        </div>
    );
}
